import {
  computed,
  inject,
  Injectable,
  Injector,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import type { GridStackNode, GridStackWidget } from 'gridstack/dist/types';
import type { Observable } from 'rxjs';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  of,
  shareReplay,
  skip,
  take,
} from 'rxjs';
import { debounceTime, map, switchMap, tap } from 'rxjs/operators';

import { debounceAsap } from '@core/common-legacy';
import { DashboardWidgetsActions, DashboardWidgetsSelectors } from '@core/dashboard-data';
import type {
  AnyWidgetDef,
  ComingSoonWidgetDef,
  FeOnlyWidgetDef,
  WidgetData,
  WidgetDef,
  WidgetId,
  WidgetLayout,
  WidgetState,
} from '@core/dashboard-types';
import {
  injectDashboardWidgets,
  isComingSoonWidgetDef,
  gridItemConfigToGridstack,
  gridDtoToGridstack,
  gridstackToGridDto,
  isFeOnlyWidgetDef,
  isSamePosition,
  nodeToWidget,
} from '@core/dashboard-util';
import { deepEqual, injectUntilDestroyed } from '@core/utils-legacy';

import { DashboardDataService } from './dashboard-data.service';

/* eslint-disable max-lines */

@Injectable({ providedIn: 'root' })
export class OverviewWidgetService {
  private readonly store = inject(Store);
  private readonly injector = inject(Injector);
  private readonly dashboardDataService = inject(DashboardDataService);
  private readonly untilDestroyed = injectUntilDestroyed();

  public overviewWidgetState = signal<Record<WidgetId, WidgetState> | null>(null);
  public gridstackState = new BehaviorSubject<GridStackNode[]>([]);

  public isWidgetsDataLoaded = this.store.selectSignal(DashboardWidgetsSelectors.isLoaded);
  public isWidgetsComponentsLoaded = signal<boolean>(false);
  public isLoaded = computed(() => this.isWidgetsDataLoaded() && this.isWidgetsComponentsLoaded());
  /**
   * @desc Widgets registered in the application
   */
  public registeredWidgets$ = combineLatest(injectDashboardWidgets());
  public availableWidgets$ = this.getAvailableWidgets();
  /**
   * @desc Widgets visible in drawer
   */
  public drawerWidgets$ = this.getDrawerWidgets();
  public feWidgets$ = this.getFeOnlyWidgets();
  /**
   * @desc Widgets visible on the overview page
   */
  public overviewWidgets = toSignal(this.getOverviewWidgets(), { initialValue: [] });

  private allWidgets = toSignal(this.registeredWidgets$, { initialValue: [] });
  private currentWidgetsData: WidgetData[] = [];

  constructor() {
    this.watchWidgetsData();

    if (!this.overviewWidgetState()) {
      this.dashboardDataService.loadOverviewWidgets();
    }

    this.gridstackState
      .pipe(
        skip(1),
        map(nodes =>
          nodes.map(node => nodeToWidget(node)).sort((a, b) => (a.id! > b.id! ? 1 : -1)),
        ),
        distinctUntilChanged((a, b) => deepEqual(a, b)),
        map(nodes => {
          const nodesMap = Object.fromEntries(nodes.map(node => [node.id, node]));
          const state = this.overviewWidgetState() ?? [];

          const stateEntries = Object.entries(state).map(([id, widgetState]) => {
            const node = nodesMap[id];
            const grid = node ?? widgetState.grid;
            return [id, { id, grid, isEnabled: widgetState.isEnabled }];
          });
          const updatedState = Object.fromEntries(stateEntries);
          this.overviewWidgetState.set(updatedState);
          return updatedState;
        }),
        debounceTime(0),
        takeUntilDestroyed(),
      )
      .subscribe(state => {
        this.saveWidgetsState(state);
      });
  }

  public saveWidgetsState(state: Record<WidgetId, WidgetState>) {
    const updatePayload = this.sortWidgetsData(
      Object.values(state).map(widget => ({
        codename: <WidgetData['codename']>widget.id,
        grid: gridstackToGridDto(widget.grid),
        enabled: widget.isEnabled,
      })),
    ).filter(item => !isFeOnlyWidgetDef(this.getWidgetDef(item.codename)!));
    const isStateEqual = deepEqual(updatePayload, this.currentWidgetsData);
    if (isStateEqual) return;

    this.store.dispatch(DashboardWidgetsActions.update({ payload: updatePayload }));
  }

  public resetWidgets() {
    this.isWidgetsComponentsLoaded.set(false);
    this.dashboardDataService
      .getOverviewWidgets({ forceInitial: true })
      .pipe(take(1), this.untilDestroyed)
      .subscribe(widgets => {
        this.overviewWidgetState.set(null);
        this.setWidgetsState(widgets, this.allWidgets());
      });
  }

  public toggleWidget(widgetId: string): void {
    this.overviewWidgetState.update(state => {
      const { [widgetId]: widgetState, ...rest } = state!;
      const isEnabled = !widgetState.isEnabled;
      const { x, y, ...grid } = widgetState.grid;
      return {
        ...rest,
        [widgetId]: {
          ...widgetState,
          grid: isEnabled ? grid : { ...grid, x, y },
          isEnabled,
        },
      };
    });
  }

  public onWidgetHeightChange(id: WidgetId, rows: number) {
    this.overviewWidgetState.update(state => {
      const widgetState = state![id];
      return {
        ...state!,
        [id]: {
          ...widgetState,
          grid: { ...widgetState.grid, h: rows, minH: rows, maxHeight: rows },
        },
      };
    });
  }

  public onWidgetsAdd(nodes: GridStackNode[]) {
    nodes.forEach(node => {
      const widgetDef = this.getWidgetDef(node.id!);
      if (!widgetDef) return;

      widgetDef.grid.onInit?.(node);
    });
    if (nodes.length === 1) {
      nodes[0].el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private getOverviewWidgets(): Observable<WidgetLayout[]> {
    return combineLatest([
      this.drawerWidgets$,
      this.feWidgets$,
      toObservable(this.overviewWidgetState),
    ]).pipe(
      debounceAsap(),
      switchMap(([drawerWidgets, feOnlyWidgets, widgetsState]) => {
        const widgetDefs = widgetsState
          ? <WidgetDef[]>(
              drawerWidgets.filter(
                widget => widgetsState[widget.id]?.isEnabled && !isComingSoonWidgetDef(widget),
              )
            )
          : [];
        const widgets = [...widgetDefs, ...feOnlyWidgets];
        if (!this.overviewWidgets().length) {
          this.isWidgetsComponentsLoaded.set(false);
        }
        return widgets.length
          ? combineLatest(
              widgets.map(widgetDef =>
                widgetDef.grid.component.pipe(
                  map(component => ({
                    component,
                    id: widgetDef.id,
                    grid: widgetsState![widgetDef.id].grid,
                  })),
                ),
              ),
            )
          : of([]);
      }),
      tap(() => this.isWidgetsComponentsLoaded.set(true)),
    );
  }

  private getAvailableWidgets() {
    return this.registeredWidgets$.pipe(
      switchMap(registeredWidgets =>
        registeredWidgets.length
          ? combineLatest(
              runInInjectionContext(this.injector, () =>
                registeredWidgets.map(widgetDef =>
                  (widgetDef.isAvailable?.() ?? of(true)).pipe(
                    map(isAvailable => ({ isAvailable, widgetDef })),
                  ),
                ),
              ),
            )
          : of([]),
      ),
      map(widgets =>
        widgets.filter(({ isAvailable }) => isAvailable).map(({ widgetDef }) => widgetDef),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private getDrawerWidgets() {
    return this.availableWidgets$.pipe(
      switchMap(registeredWidgets =>
        this.dashboardDataService
          .getOverviewWidgets()
          .pipe(
            map(dashboardWidgets =>
              this.filterRegisteredWidgets(registeredWidgets, dashboardWidgets),
            ),
          ),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private getFeOnlyWidgets() {
    return this.availableWidgets$.pipe(
      map(registeredWidgets => registeredWidgets.filter(widget => isFeOnlyWidgetDef(widget))),
    );
  }

  private filterRegisteredWidgets(
    registeredWidgets: AnyWidgetDef[],
    dashboardWidgets: WidgetData[],
  ) {
    return <Array<ComingSoonWidgetDef | WidgetDef>>(
      registeredWidgets.filter(
        registered =>
          isComingSoonWidgetDef(registered) ||
          dashboardWidgets.some(widgetData => registered.id === widgetData.codename),
      )
    );
  }

  private getWidgetDef(widgetId: string): WidgetDef | null {
    const widget = <WidgetDef | undefined>(
      this.allWidgets().find(widget => widget.id === widgetId && !isComingSoonWidgetDef(widget))
    );
    return widget ?? null;
  }

  private watchWidgetsData() {
    combineLatest([this.dashboardDataService.getOverviewWidgets(), this.registeredWidgets$])
      .pipe(debounceAsap(), this.untilDestroyed)
      .subscribe(([widgets, allWidgets]) => {
        this.currentWidgetsData = this.sortWidgetsData(widgets);
        this.setWidgetsState(widgets, allWidgets);
      });
  }

  private sortWidgetsData(widgets: WidgetData[]) {
    return [...widgets].sort((a, b) => (a.codename > b.codename ? 1 : -1));
  }

  private setWidgetsState(widgets: WidgetData[], allWidgets: AnyWidgetDef[]) {
    const feWidgetEntries = allWidgets.filter(isFeOnlyWidgetDef).map(widgetDef => {
      const prevItem = this.overviewWidgetState()?.[widgetDef.id];
      return [
        widgetDef.id,
        {
          id: widgetDef.id,
          isEnabled: true,
          grid: prevItem?.grid ?? this.getGridConfig(widgetDef, prevItem?.grid ?? null),
        } satisfies WidgetState,
      ];
    });
    const widgetEntries = widgets
      .map(widget => {
        const widgetDef = this.getWidgetDef(widget.codename);
        if (!widgetDef) return null;

        const prevItem = this.overviewWidgetState()?.[widgetDef.id];
        const newItem = this.createWidget(widgetDef, widget, gridDtoToGridstack(widget.grid));
        return [
          widget.codename,
          {
            ...newItem,
            grid:
              prevItem && isSamePosition(newItem.grid, prevItem.grid)
                ? prevItem.grid
                : newItem.grid,
          } satisfies WidgetState,
        ];
      })
      .filter(entry => !!entry);
    const widgetsWithDefaults = Object.fromEntries([...widgetEntries, ...feWidgetEntries]);
    this.overviewWidgetState.set(widgetsWithDefaults);
  }

  private createWidget(
    widgetDef: WidgetDef,
    widgetData: WidgetData,
    grid: GridStackWidget | null,
  ): WidgetState {
    const id = widgetData.codename;
    return {
      id,
      isEnabled: widgetData.enabled,
      grid: this.getGridConfig(widgetDef, grid),
    };
  }

  private getGridConfig(
    widgetDef: WidgetDef | FeOnlyWidgetDef,
    prevGrid: GridStackWidget | null,
  ): GridStackWidget {
    return {
      ...gridItemConfigToGridstack(widgetDef.grid.config),
      ...prevGrid,
      id: widgetDef.id,
    };
  }
}
